import { useEffect, useState } from "react";
import { Sparkles, RefreshCw, Share2, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatApiError, fmtDate } from "@/lib/api";

const PROVIDERS = [{ value: "openai", label: "ChatGPT" }, { value: "gemini", label: "Gemini" }];

export function InsightText({ text }) {
  return (
    <div className="text-sm text-slate-700 leading-relaxed flex flex-col gap-1.5">
      {text.split("\n").filter((l) => l.trim()).map((l, i) =>
        l.startsWith("#") ? <h4 key={i} className="mt-2 font-semibold text-[#0B2A4A]">{l.replace(/^#+\s*/, "").replace(/\*\*/g, "")}</h4>
          : /^\s*[-*] /.test(l) ? <p key={i} className="pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-[#1A9AA8]">{l.trim().slice(2).replace(/\*\*/g, "")}</p>
            : <p key={i}>{l.replace(/\*\*/g, "")}</p>)}
    </div>
  );
}

export function useInsights(studentId) {
  const [list, setList] = useState([]);
  const load = () => api.get(`/admin/students/${studentId}/insights`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { if (studentId) load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { list, load };
}

async function streamInsight(studentId, scope, provider, onChunk) {
  const res = await fetch(`${API}/admin/students/${studentId}/insights/stream`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope, provider }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "AI request failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    onChunk(dec.decode(value, { stream: true }));
  }
}

export function AiInsights({ studentId, scope, list, reload, title }) {
  const shared = list.find((i) => i.scope === scope);
  const [text, setText] = useState("");
  const [provider, setProvider] = useState("openai");
  const [busy, setBusy] = useState(false);
  const tid = `ai-insights-${scope.replace(":", "-")}`;

  const generate = async () => {
    setBusy(true);
    setText("");
    try {
      await streamInsight(studentId, scope, provider, (c) => setText((t) => t + c));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const share = () => api.post(`/admin/students/${studentId}/insights`, { scope, body: text }).then(() => { toast.success("Shared with the student"); reload(); }).catch((e) => toast.error(formatApiError(e)));
  const unshare = () => api.delete(`/admin/insights/${shared.id}`).then(() => { toast.success("Removed from the student's view"); reload(); }).catch((e) => toast.error(formatApiError(e)));

  return (
    <div className="rounded-2xl border border-[#F08A3C]/40 bg-gradient-to-br from-[#FFF7EF] to-white p-5" data-testid={tid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-semibold text-[#0B2A4A]"><Sparkles className="h-4 w-4 text-[#F08A3C]" /> {title || "AI teaching insights"}</p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-[#0B2A4A]/15 overflow-hidden text-xs" role="group">
            {PROVIDERS.map((p) => (
              <button key={p.value} type="button" onClick={() => setProvider(p.value)} aria-pressed={provider === p.value}
                className={`px-2.5 py-1.5 font-semibold ${provider === p.value ? "bg-[#0B2A4A] text-white" : "bg-white text-[#0B2A4A]"}`} data-testid={`${tid}-provider-${p.value}`}>{p.label}</button>
            ))}
          </div>
          <button onClick={generate} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-[#F08A3C] text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid={`${tid}-generate`}>
            {text ? <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> : <Sparkles className="h-3.5 w-3.5" />} {busy ? "Thinking..." : text ? "Generate again" : "Get AI insights"}
          </button>
          {text && !busy && <button onClick={share} className="inline-flex items-center gap-1.5 rounded-lg bg-[#1A9AA8] text-white px-3 py-1.5 text-xs font-semibold" data-testid={`${tid}-share`}><Share2 className="h-3.5 w-3.5" /> Share with student</button>}
        </div>
      </div>
      {text ? <div className="mt-3" data-testid={`${tid}-text`}><InsightText text={text} /></div>
        : <p className="mt-2 text-xs text-slate-500">AI reads the student's answers and suggests what they're working toward, strengths, gaps and teaching ideas. Only you see it unless you share it.</p>}
      {shared && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid={`${tid}-shared`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold text-emerald-800">Shared with student · {fmtDate(shared.updated_at)}</p>
            <button onClick={unshare} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600" data-testid={`${tid}-unshare`}><EyeOff className="h-3.5 w-3.5" /> Stop sharing</button>
          </div>
          {!text && <div className="mt-2"><InsightText text={shared.body} /></div>}
        </div>
      )}
    </div>
  );
}

export function SharedInsight({ insight, title }) {
  if (!insight) return null;
  return (
    <div className="rounded-2xl border border-[#F08A3C]/40 bg-gradient-to-br from-[#FFF7EF] to-white p-6" data-testid={`shared-insight-${insight.scope.replace(":", "-")}`}>
      <p className="flex items-center gap-2 font-semibold text-[#0B2A4A]"><Sparkles className="h-4 w-4 text-[#F08A3C]" /> {title}</p>
      <p className="text-xs text-slate-500 mt-1">AI-assisted notes Mr. Greg shared from your answers.</p>
      <div className="mt-3"><InsightText text={insight.body} /></div>
    </div>
  );
}

export function useMyInsights() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/course/insights").then((r) => setList(r.data)).catch(() => {}); }, []);
  return list;
}
